import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Globe, Music, Trash2, Image as ImageIcon } from "lucide-react";

const KIND_FILTERS = [
  { value: "all", label: "All" },
  { value: "token", label: "Tokens" },
  { value: "map", label: "Maps" },
  { value: "music", label: "Music" },
  { value: "sfx", label: "SFX" },
];

const ASSETS_BG_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_adventure-hub-220/artifacts/aq4dluss_assets.png";

// Home-page "Assets" library: every file this GM has ever uploaded, across ALL their campaigns.
// Anything uploaded inside a campaign stays scoped to that campaign by default — promoting it
// here makes it available in every campaign's in-game Assets/SFX tabs automatically.
export default function AssetsLibraryPage() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [kind, setKind] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/uploads/library", { params: kind === "all" ? {} : { kind } }).then((r) => setAssets(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [kind]);

  const togglePool = async (asset) => {
    await api.patch(`/uploads/${asset.id}`, { game_id: asset.is_master_pool ? asset.home_game_id : null });
    toast.success(asset.is_master_pool ? `Returned to "${asset.home_game_name}" only` : "Promoted to your Master Pool — usable in every campaign now");
    load();
  };

  const deleteAsset = async (id) => {
    await api.delete(`/uploads/${id}`);
    load();
  };

  const groups = assets.reduce((acc, a) => {
    const key = a.is_master_pool ? "Master Pool" : a.home_game_name || "Unassigned";
    (acc[key] = acc[key] || []).push(a);
    return acc;
  }, {});
  const groupNames = Object.keys(groups).sort((a) => (a === "Master Pool" ? -1 : 1));

  return (
    <div className="relative min-h-screen text-[#f1e0c5] bg-[#151613] bg-cover bg-center bg-fixed" style={{ backgroundImage: `url('${ASSETS_BG_URL}')` }} data-testid="assets-library-page">
      <div className="fixed inset-0 bg-black/70 pointer-events-none" data-testid="assets-lib-bg-overlay" />
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6 border-b border-white/5">
        <Link to="/" className="font-display text-2xl flex items-center gap-2.5"><img src="/logo192.png" alt="Call to Arms" className="w-8 h-8 rounded-md" data-testid="brand-logo-icon" />Call to Arms<span className="text-[#D90429]">.</span></Link>
      </nav>
      <div className="relative z-10 px-6 md:px-12 py-10 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button data-testid="assets-library-back-button" variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-display text-4xl mb-1">Your Assets</h1>
            <p className="text-sm text-[#f1e0c5]/50">Every image and sound you've uploaded, across every table. Promote one to your Master Pool to use it anywhere.</p>
          </div>
        </div>

        <div className="flex gap-1.5 mb-8">
          {KIND_FILTERS.map((k) => (
            <button
              key={k.value}
              data-testid={`assets-library-filter-${k.value}`}
              onClick={() => setKind(k.value)}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border ${kind === k.value ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-white/10 text-[#f1e0c5]/50 hover:border-[#D4AF37]/50"}`}
            >
              {k.label}
            </button>
          ))}
        </div>

        {!loading && assets.length === 0 && (
          <div className="text-center py-20 text-[#f1e0c5]/40" data-testid="assets-library-empty-state">
            <ImageIcon className="w-10 h-10 mx-auto mb-4 opacity-40" />
            Nothing uploaded yet — upload art or sound from inside any of your games.
          </div>
        )}

        {groupNames.map((name) => (
          <div key={name} className="mb-8">
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37]/70 mb-3 flex items-center gap-1.5">
              {name === "Master Pool" && <Globe className="w-3.5 h-3.5" />} {name} ({groups[name].length})
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {groups[name].map((a) => {
                const isAudio = (a.content_type || "").startsWith("audio");
                return (
                  <div key={a.id} data-testid={`library-asset-${a.id}`} className="rounded-xl border border-white/10 bg-black/30 overflow-hidden">
                    <div className="aspect-square bg-black/40 flex items-center justify-center">
                      {isAudio ? <Music className="w-6 h-6 text-[#D4AF37]/50" /> : <img src={fileUrl(a.id)} alt={a.original_filename} className="w-full h-full object-cover" />}
                    </div>
                    <div className="p-2 space-y-1.5">
                      <p className="text-[10px] truncate" title={a.original_filename}>{a.original_filename}</p>
                      <div className="flex items-center justify-between gap-1">
                        <button
                          data-testid={`library-asset-toggle-pool-${a.id}`}
                          onClick={() => togglePool(a)}
                          className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full border ${a.is_master_pool ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-[#f1e0c5]/40 hover:border-[#D4AF37]/50"}`}
                        >
                          {a.is_master_pool ? "In Pool" : "Promote"}
                        </button>
                        <button data-testid={`library-asset-delete-${a.id}`} onClick={() => deleteAsset(a.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429]">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
